// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useParams } from "react-router-dom"
import { useCycleLatestSnapshot } from "@/queries/queries.eventJournal"
import FitnessDistributionChart from "./FitnessDistributionChart"
import { summarizeAllIters } from "./summarize"
import { parseCycleSnapshotPayload } from "../_snapshotPayload"
import { deriveReferenceSeries } from "../referenceSet/derive"
import ChartZeroState from "../ChartZeroState"

export interface FitnessChartsProps {
  cycleId?: string
}

export default function FitnessCharts({ cycleId }: FitnessChartsProps = {}) {
  const params = useParams<{ cycleId: string }>()
  const resolvedCycleId = cycleId ?? params.cycleId ?? ""

  const snapshotQuery = useCycleLatestSnapshot(
    resolvedCycleId.length > 0 ? resolvedCycleId : undefined,
  )

  // Render nothing while the snapshot read is in flight (no zero-state
  // flash before its own source resolves).
  if (snapshotQuery.isLoading) {
    return null
  }

  // Per-panel gating (issue `per-panel-gating-t0`): once the read has
  // resolved without a snapshot row, the panel owns its own zero-state
  // instead of leaning on the retired global shell gate (US §10).
  if (!snapshotQuery.isSuccess || snapshotQuery.data == null) {
    return <ChartZeroState message="No fitness data yet." />
  }

  const dto = parseCycleSnapshotPayload<CycleSnapshotDto>(
    snapshotQuery.data.payload_json,
  )
  if (dto === null) return null

  const summaries = summarizeAllIters(dto.all_scores_by_iter ?? [])
  const referenceSeries = deriveReferenceSeries(dto)

  return (
    <div className="w-full" aria-label="Fitness charts">
      <FitnessDistributionChart
        summaries={summaries}
        referenceSeries={referenceSeries}
      />
    </div>
  )
}
